import groq from 'groq'

import { complexContentFragment, simpleContentFragment } from './content'
import { imageFragment } from './image'
import { linkFragment, linkPageFragment } from './link'
import { productFragment } from './product'

const headerDesktopMenuFragment = groq`
  "slug": slug.current,
  items[] {
    ${linkFragment}
    dropdownItems[] {
      ${linkFragment}
    },
    featured[]->{
      ${productFragment}
    },
  },
`

const headerMobileMenuFragment = groq`
  "slug": slug.current,
  items[] {
    ${linkFragment}
    dropdownItems[] {
      ${linkFragment}
    },
  },
`

const headerPromoFragment = groq`
  enabled,
  display,
  text,
  "link": link->{
    ${linkPageFragment}
  },
`

const headerFragment = groq`
  menuDesktopLeft->{
    ${headerDesktopMenuFragment}
  },
  menuDesktopRight->{
    ${headerDesktopMenuFragment}
  },
  menuMobilePrimary->{
    ${headerMobileMenuFragment}
  },
  menuMobileSecondary->{
    ${headerMobileMenuFragment}
  },
  "promo": *[
    _type == "promoSettings" &&
    locale == $locale
  ][0] {
    ${headerPromoFragment}
  },
`

export const newsletterFragment = groq`
  ...,
  successMsg[] {
    ${complexContentFragment}
  },
  errorMsg[] {
    ${complexContentFragment}
  },
  terms[] {
    ${simpleContentFragment}
  },
`

const footerFragment = groq`
  content[] {
    ${complexContentFragment}
  },
  menu->{
    items[] {
      ${linkFragment}
    },
  },
  social[] {
    _key,
    socialNetwork,
    url,
  },
  copyright,
`

const productCountsFragment = groq`
  "slug": slug.current,
  "count": count(products),
`

const cartFragment = groq`
  ...,
  terms[] {
    ${simpleContentFragment}
  },
`

const cookieConsentFragment = groq`
  enabled,
  message,
  "link": link->{
    ${linkPageFragment}
  },
`

const siteStringsFragment = groq`
  loadingPageTitle,
  languageSwitch,
  cartLink,
  featuredProducts,
  skipToContent,
  goHomeLabel,
  mobileMenuLabel,
  productLowStock,
  productOutOfStock,
  productPriceLabel,
  productColorOptionLabel,
  productDiscountText,
  productIncreaseQuantity,
  productDecreaseQuantity,
  productEnterQuantity,
  cartTitle,
  cartClose,
  cartEmpty,
  cartVatSectionTitle,
  cartVatIdInputPlaceholder,
  cartVatIdError,
  cartInvalidVatIdError,
  cartCommentSectionTitle,
  cartCommentInputPlaceholder,
  cartDiscount,
  cartSubtotal,
  cartTotal,
  cartSubmit,
  cartAddToCartErrorMessage[] {
    ${complexContentFragment}
  },
  carouselCounterText,
  carouselLeftArrowLabel,
  carouselRightArrowLabel,
  carouselDotLabel,
  buttonAddToCart,
  buttonRemove,
  buttonAccept,
  buttonLearnMore,
  buttonTryAgain,
  buttonLoadMore,
  buttonUnavailable,
  buttonWaiting,
  buttonLoading,
  buttonAdding,
  buttonUpdating,
  buttonSubmitting,
  buttonEdit,
  buttonDelete,
  buttonCancel,
  emailAddress,
  emailAddressPlaceholder,
  emailMissing,
  emailInvalid,
  firstName,
  firstNamePlaceholder,
  firstNameMissing,
  lastName,
  lastNamePlaceholder,
  lastNameMissing,
  fullName,
  fullNamePlaceholder,
  fullNameMissing,
  phoneNumber,
  phoneNumberPlaceholder,
  phoneNumberMissing,
  phoneNumberInvalid,
  company,
  companyPlaceholder,
  message,
  messagePlaceholder,
  productWaitlistSubmit,
  productWaitlistSuccess,
  productWaitlistError,
  blogPostReadMore,
  blogPostBy,
  teamMemberCardLinkedIn,
  teamMemberCardBlogArticles,
`

const identityFragment = groq`
  logo {
    ${imageFragment}
  },
  invertedLogo {
    ${imageFragment}
  },
`

// Only settings that are not secret and can be used in the frontend
const publicSettingsFragment = groq`
  siteURL,
  hubSpotPortalId,
  klaviyoAccountID,
  gtmContainerId,
  analyticsId,
  facebookEvents,
  facebookPixelId,
  facebookDomainVerification,
  facebookTestEventCode,
  stampedApiKey,
  partnerAdsTracking,
  cookieBotId,
  pageAnimation,
`

const discountFragment = groq`
  _id,
  type,
  title,
  discountValuePercent,
  ...select(type == 'quantity' => {
    minimumQuantity,
    maximumQuantity,
  }),
  ...select(type == 'bundle' => {
    products[] {
      "id": selection.id,
      "variantIds": selection.variants[selected == true].id,
    },
    doNotStackWithQuantityDiscounts,
  }),
`

export const publicSiteSettingsQuery = groq`
  *[_type == "generalSettings" && locale == $locale][0] {
    ${publicSettingsFragment}
  }
`

export const allSiteSettingsQuery = groq`
  *[_type == "generalSettings" && locale == $locale][0]
`

export const discountQuery = groq`
  *[_type == "discount" && locale == $locale && enabled == true] {
    ${discountFragment}
  }
`

export const siteFragment = groq`
  "publicLocales": *[
    _type == "generalSettings" &&
    public == true
  ][].locale,
  "settings": *[
    _type == "generalSettings" &&
    locale == $locale
  ][0] {
    ${publicSettingsFragment}
  },
  "identity": *[
    _type == "identitySettings" &&
    locale == $locale
  ][0] {
    ${identityFragment}
  },
  "cart": *[
    _type == "cartSettings" &&
    locale == $locale
  ][0] {
    ${cartFragment}
  },
  "blogSettings": *[
    _type == "blogSettings" &&
    locale == $locale
  ][0],
  "productCounts": [{
    "slug": "all",
    "count": count(*[
      _type == "product" &&
      locale == $locale
    ])
  }] + *[
    _type == "collection" &&
    locale == $locale
  ] {
    ${productCountsFragment}
  },
  "cookieConsent": *[
    _type == "cookieSettings" &&
    locale == $locale
  ][0] {
    ${cookieConsentFragment}
  },
  "header": *[
    _type == "headerSettings" &&
    locale == $locale
  ][0] {
    ${headerFragment}
  },
  "footer": *[
    _type == "footerSettings" &&
    locale == $locale
  ][0] {
    ${footerFragment}
  },
  "seo": *[
    _type == "seoSettings" &&
    locale == $locale
  ][0],
  "siteStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${siteStringsFragment}
  },
  "discounts": *[
    _type == "discount" &&
    locale == $locale &&
    enabled == true
  ] {
    ${discountFragment}
  },
`

export const siteQuery = groq`
  {
    ${siteFragment}
  }
`
